import { useState, useCallback, useEffect } from 'react'
import { Info, AlertTriangle } from 'lucide-react'

import { AdasDateTimePicker, AdasRichTextEditor, AdasTypography } from '@components/wrapper-components'
import { useUserStore } from '@caradasstore/UserStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { BUTTONS_DISPLAY_LABEL, GENERIC_MESSAGES, RadioGroup_Constants } from '../../constants'
import './BroadcastMessagePage.css'
import dayjs from 'dayjs'
import { ConfirmModal } from '@components/common'
import { sendBroadcastNotification, updateBroadcastNotification, getActiveBroadcast, endBroadcast } from '../../api/api'
import { Logger } from '../../logger'
import { StatusInfo } from '@components/status-info'
import { BroadcastBanner } from '@components/broadcastBar-bar/BroadcastBanner'

const TYPE_ICON = { INFO: Info, CRITICAL: AlertTriangle }

const FIELD_LABEL = 'mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'
const OUTLINE_BTN =
  'inline-flex items-center justify-center rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-sm font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
const PRIMARY_BTN =
  'inline-flex items-center justify-center rounded-lg bg-sidebar px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'

export const BroadcastMessagePage = () => {
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showEndBroadcastModal, setShowEndBroadcastModal] = useState(false)
  const [messageId, setMessageId] = useState()
  const [messageContent, setMessageContent] = useState('')
  const [subject, setSubject] = useState('')
  const [isMessageVisible, setIsMessageVisible] = useState(false)
  const [selectedType, setSelectedType] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [adminCheckFailureMessage, SetAdminCheckFailureMessage] = useState(null)
  const currentUser = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)

  const isRichTextEditorEmpty = (content) => {
    const text = content.replace(/<[^>]*>/g, '')
    const trimmedText = text.trim()
    return trimmedText.length === 0
  }
  const validateForm = useCallback(() => {
    if (!errorMessage && selectedType && subject && messageContent && !isRichTextEditorEmpty(messageContent)) {
      setIsFormValid(true)
    } else {
      setIsFormValid(false)
    }
  }, [selectedType, subject, messageContent, errorMessage])

  useEffect(() => {
    validateForm()
  }, [selectedType, subject, messageContent, errorMessage])

  const handleTypeChange = useCallback((event) => {
    setSelectedType(event.target.value)
  }, [])

  const handleEndBroadCast = useCallback(async () => {
    try {
      await endBroadcast(messageId)
      setShowEndBroadcastModal(false)
      setMessageId('')
      setMessageContent('')
      setSubject('')
      setSelectedType('')
      setStartDate()
      setEndDate()
      setIsFormValid(false)
      setIsEditMode(false)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'handleEndBroadCast', messageId } })
    }
  }, [messageId])

  const handleClearForm = useCallback(async () => {
    setShowEndBroadcastModal(false)
    setMessageContent('')
    setSubject('')
    setSelectedType('')
    setStartDate()
    setEndDate()
    setErrorMessage('')
    setIsFormValid(false)
  })

  const handleCancelForm = () => {
    setIsEditMode(false)
  }

  const modalContinue = useCallback(
    (code) => {
      const sendNotification = async () => {
        try {
          setLoading(true)
          const notificationData = {
            message: messageContent,
            subject: subject,
            type: selectedType,
            start_at: startDate?.toISOString() || null,
            end_at: endDate?.toISOString() || null,
            created_by: currentUser.id,
            created_at: dayjs().toISOString()
          }

          const sendNotificationData = await sendBroadcastNotification(notificationData)
          if (sendNotificationData) {
            setMessageId(sendNotificationData.data.id)
          }
        } catch (error) {
          Logger.error({ message: error, payload: { file: 'broadcastmessage', message: messageContent } })
        } finally {
          setLoading(false)
        }
      }

      const updateNotification = async () => {
        try {
          setLoading(true)
          const notificationData = {
            id: messageId,
            message: messageContent,
            subject: subject,
            type: selectedType,
            start_at: startDate?.toISOString() || null,
            end_at: endDate?.toISOString() || null,
            created_by: currentUser.id,
            created_at: dayjs().toISOString()
          }

          const sendNotificationData = await updateBroadcastNotification({ broadcastId: messageId, data: notificationData })
          if (sendNotificationData) {
            setMessageId(sendNotificationData.data.id)
          }
        } catch (error) {
          Logger.error({ message: error, payload: { file: 'broadcastmessage', message: messageContent } })
        } finally {
          setLoading(false)
          setIsEditMode(false)
        }
      }

      if (code === '2180') {
        if (messageId) {
          updateNotification()
        } else {
          sendNotification()
        }
        SetAdminCheckFailureMessage(null)
        setShowModal(false)
      } else {
        SetAdminCheckFailureMessage(GENERIC_MESSAGES.INCORRECT_ADMIN_CODE)
        setShowModal(true)
      }
    },
    [isFormValid, messageId, messageContent, subject, selectedType, currentUser]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        const broadcastResponse = await getActiveBroadcast()
        if (broadcastResponse?.data) {
          const broadcastData = broadcastResponse?.data
          setMessageId(broadcastData.id)
          setSelectedType(broadcastData.type)
          setMessageContent(broadcastData.message)
          setSubject(broadcastData.subject)
          setStartDate(broadcastData.start_at ? dayjs(broadcastData.start_at) : null)
          setEndDate(broadcastData.end_at ? dayjs(broadcastData.end_at) : null)
        }
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'broadcastmessage' } })
      }
    }

    fetchData()
  }, [])

  const handleConfirmModal = useCallback(() => setShowModal(true))
  const handleBroadcastEndModal = useCallback(() => setShowEndBroadcastModal(true))

  const handleStartDateChange = useCallback(
    (newDate) => {
      if (endDate && dayjs(newDate).isAfter(dayjs(endDate))) {
        setErrorMessage('Start date must be less than end date')
      } else if (endDate && dayjs(newDate).isSame(dayjs(endDate))) {
        setErrorMessage('Start date and end date should not be equal')
      } else {
        setErrorMessage('')
      }
      setStartDate(newDate)
    },
    [endDate]
  )

  const handleEndDateChange = useCallback(
    (newDate) => {
      if (startDate && dayjs(newDate).isBefore(dayjs(startDate))) {
        setErrorMessage('End date must be greater than start date')
      } else if (startDate && dayjs(newDate).isSame(dayjs(startDate))) {
        setErrorMessage('Start date and end date should not be equal')
      } else {
        setErrorMessage('')
      }
      setEndDate(newDate)
    },
    [startDate]
  )

  const handleSubjectChange = (event) => {
    setSubject(event.target.value)
  }

  const handleEditorChange = useCallback((content) => {
    setMessageContent(content)
  }, [])

  const handleButtonClick = useCallback(() => {
    setIsMessageVisible(true) // Set visibility to true when viewing message
  }, [messageContent])

  const handleEditClick = useCallback(() => {
    setIsEditMode(true)
  }, [])

  const fieldsDisabled = !isEditMode && !!messageId

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 pb-24 min-[435px]:px-0 min-[435px]:pb-6'>
      {/* Wrapper: card chrome on desktop, flush on mobile to match the screen mock. */}
      <div className='rounded-none border-0 bg-transparent p-0 shadow-none min-[435px]:rounded-2xl min-[435px]:border min-[435px]:border-[#EBEDF0] min-[435px]:bg-white min-[435px]:p-6 min-[435px]:shadow-sm'>
        {/* TYPE */}
        <div className='mb-1.5 flex items-center justify-between'>
          <span className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Type</span>
          {messageId && <StatusInfo color='primary' label='Active' />}
        </div>
        <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
          {RadioGroup_Constants.map((item) => {
            const Icon = TYPE_ICON[item.value] || Info
            const selected = selectedType === item.value
            const isCritical = item.value === 'CRITICAL'
            return (
              <button
                key={item.value}
                type='button'
                disabled={fieldsDisabled}
                onClick={() => handleTypeChange({ target: { value: item.value } })}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors disabled:pointer-events-none disabled:opacity-60 ${
                  selected ? 'border-[#1F6FEB] bg-white' : 'border-[#D1D5D8] bg-white hover:bg-[#F8FAFC]'
                }`}
              >
                <span
                  className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg ${
                    isCritical ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#EFF6FF] text-[#1F6FEB]'
                  }`}
                >
                  <Icon className='h-4 w-4' />
                </span>
                <span className='flex-1 text-[14px] font-bold text-[#0A1628]'>{item.label}</span>
                <span
                  className={`inline-flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 ${
                    selected ? 'border-[#1F6FEB]' : 'border-[#9CA3AF]'
                  }`}
                >
                  {selected && <span className='h-2.5 w-2.5 rounded-full bg-[#1F6FEB]' />}
                </span>
              </button>
            )
          })}
        </div>

        {/* SUBJECT */}
        <div className='mt-5'>
          <label htmlFor='broadcast-subject' className={FIELD_LABEL}>
            Subject
          </label>
          <input
            id='broadcast-subject'
            name='subject'
            value={subject}
            onChange={handleSubjectChange}
            disabled={fieldsDisabled}
            className='h-11 w-full rounded-lg border border-[#D1D5D8] px-3 text-[14px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none disabled:bg-[#F8FAFC] disabled:opacity-70'
          />
        </div>

        {/* MESSAGE */}
        <div className='mt-5'>
          <AdasRichTextEditor disabled={fieldsDisabled} value={messageContent} onChange={handleEditorChange} />
        </div>

        {/* PREVIEW */}
        <div className='mt-4'>
          <button type='button' disabled={fieldsDisabled} onClick={handleButtonClick} className={`${OUTLINE_BTN} px-4 py-2 text-[13px]`}>
            Preview Message
          </button>
        </div>

        {/* CAR-4102: the same banner users see. Closing the preview only hides it - nothing is remembered. */}
        {isMessageVisible && (
          <div className='mt-4'>
            <BroadcastBanner type={selectedType} subject={subject} message={messageContent} onDismiss={() => setIsMessageVisible(false)} />
          </div>
        )}

        {/* DATES */}
        <div className='mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <div>
            <span className={FIELD_LABEL}>Start Date and Time</span>
            <AdasDateTimePicker
              disabled={fieldsDisabled}
              name='start_date'
              disableFuture={false}
              value={startDate}
              onChange={handleStartDateChange}
              className='w-full'
            />
            {errorMessage && <p className='mt-2 text-[13px] text-[#DC2626]'>{errorMessage}</p>}
          </div>
          <div>
            <span className={FIELD_LABEL}>End Date and Time</span>
            <AdasDateTimePicker
              disabled={fieldsDisabled}
              name='end_date'
              disableFuture={false}
              value={endDate}
              onChange={handleEndDateChange}
              className='w-full'
            />
          </div>
        </div>

        {/* FOOTER — sticky bottom bar on mobile, inline justify-end on desktop. */}
        <div className='fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-[#EBEDF0] bg-white px-4 py-3 min-[435px]:static min-[435px]:mt-6 min-[435px]:justify-end min-[435px]:border-0 min-[435px]:bg-transparent min-[435px]:p-0'>
          {messageId ? (
            !isEditMode ? (
              <>
                <button type='button' onClick={handleEditClick} className={`${OUTLINE_BTN} flex-none`}>
                  Edit
                </button>
                <button
                  type='button'
                  onClick={handleBroadcastEndModal}
                  disabled={!isFormValid}
                  className={`${PRIMARY_BTN} flex-1 min-[435px]:flex-none`}
                >
                  End Broadcast
                </button>
              </>
            ) : (
              <>
                <button type='button' onClick={handleCancelForm} className={`${OUTLINE_BTN} flex-none`}>
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={handleConfirmModal}
                  disabled={!isFormValid}
                  className={`${PRIMARY_BTN} flex-1 min-[435px]:flex-none`}
                >
                  Update Broadcast
                </button>
              </>
            )
          ) : (
            <>
              <button type='button' onClick={handleClearForm} className={`${OUTLINE_BTN} flex-none`}>
                Clear
              </button>
              <button
                type='button'
                onClick={handleConfirmModal}
                disabled={!isFormValid}
                className={`${PRIMARY_BTN} flex-1 min-[435px]:flex-none`}
              >
                Start Broadcast
              </button>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        open={showModal}
        handleClose={() => {
          SetAdminCheckFailureMessage(null)
          setShowModal(false)
        }}
        handleContinue={modalContinue}
        title={GENERIC_MESSAGES.START_BROADCAST_TITLE}
        adminCheckFailureMessage={adminCheckFailureMessage}
        message={
          <AdasTypography variant='body1' className='custom-message'>
            {GENERIC_MESSAGES.BROADCAST_INFO_MESSAGE}
          </AdasTypography>
        }
        confirmLabel={BUTTONS_DISPLAY_LABEL.CONFIRM}
        adminCheck={true}
      />
      <ConfirmModal
        open={showEndBroadcastModal}
        handleClose={() => setShowEndBroadcastModal(false)}
        handleContinue={handleEndBroadCast}
        title={GENERIC_MESSAGES.END_BROADCAST_TITLE}
        message={GENERIC_MESSAGES.END_BROADCAST_MESSAGE}
        confirmLabel={BUTTONS_DISPLAY_LABEL.YES}
      />
    </div>
  )
}
